<script setup lang="ts">
import { api } from '@/api';
import {
  ActionItemDto,
  CreateUpdateRoleDto,
  EntityWithCommentType,
  PermissionDto,
  RoleDto,
} from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs } from '@/components/pages/BasePage/defaults';
import type { TabDefinition } from '@/components/Tabs/types';
import { useAccountStore } from '@/stores/accountStore.ts';
import { getDisabledAndHiddenFlags } from '@/utils/actions';
import SelectPermissions from '@/views/roles/components/SelectPermissions.vue';
import { getTranslatedRoleName, getTranslatedRoleDescritption } from '@/views/roles/utils.ts';
import { useTranslation } from 'i18next-vue';
import { pick } from 'lodash';
import { useFormValues } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';
import ViewPermissions from './ViewPermissions.vue';
import { useAmountComments } from '@/components/comments/useAmountComments';

const { t } = useTranslation();
const values = useFormValues<RoleDto & CreateUpdateRoleDto>();
const accountStore = useAccountStore();
const mode = useFormMode();

const permissionsToLock = ref<string[]>([]);

const allPermissions = ref<PermissionDto[]>();

const { commentsChanged, commentsTabTitle } = useAmountComments(values);

const tabs = computed(
  () =>
    ({
      ...pick(defaultPageTabs, 'administrative'),
      comments: {
        hidden: mode.value === 'add',
        title: commentsTabTitle.value,
      },
    }) satisfies TabDefinition,
);

function editOwnedRole() {
  return values.value?.id && accountStore.currentUser?.assignedRoles.includes(values.value.id);
}

const canModifyAvailability = computed(
  () => !getDisabledAndHiddenFlags(values.value, ActionItemDto.SetAvailability).disabled,
);

const availabilityMessage = computed(() =>
  !canModifyAvailability.value && mode.value !== 'details'
    ? t('AvailibilityOfRoleOfCurrentUserCanNotBeChangedMsg')
    : undefined,
);

onMounted(async () => {
  const { result: allPermissionsResult } = await api.rolesApi.getAllPermissions();
  if (allPermissionsResult) allPermissions.value = allPermissionsResult;
});
</script>

<template>
  <BasePageNavigation :tabs="tabs">
    <template #administrative>
      <v-row>
        <v-col cols="12" md="6" lg="3">
          <VeeTextField
            name="displayName"
            :label="$t('Name')"
            :outputConverter="() => getTranslatedRoleName($t, values as RoleDto)"
          />
        </v-col>
        <v-col cols="12" md="6" lg="4">
          <VeeTextarea
            name="description"
            :label="$t('Description')"
            :outputConverter="() => getTranslatedRoleDescritption($t, values as RoleDto)"
          />
          <VeeAttachments
            class="mt-1"
            name="attachments"
            data-testid="Description_Attachments"
          ></VeeAttachments>
        </v-col>
        <v-col cols="12" offsetMd="3" md="2">
          <VeeAvailability
            name="isAvailable"
            :disabled="!canModifyAvailability"
            :infoMessage="availabilityMessage"
          />
          <EntityChangeBlock v-if="mode === 'details'" :entity="values" />
        </v-col>
      </v-row>

      <VDivider class="mt-4 mb-8" thickness="2"></VDivider>

      <SelectPermissions
        v-if="mode === 'edit' && values.grantedPermissions && allPermissions"
        v-model="values.grantedPermissions"
        :allPermissions="allPermissions"
        :lockedPermissions="editOwnedRole() ? permissionsToLock : []"
      ></SelectPermissions>
      <ViewPermissions
        v-if="mode === 'details' && allPermissions && values.grantedPermissions"
        :allPermissions="allPermissions || []"
        :grantedPermissions="values.grantedPermissions"
      />
    </template>
    <template #comments>
      <Comments
        v-if="values.id"
        :id="values.id.toString()"
        :entityWithCommentType="EntityWithCommentType.Role"
        @change="commentsChanged"
      />
    </template>
  </BasePageNavigation>
</template>

<style scoped lang="scss">
.card {
  min-height: 100%;
}
</style>
@/utils/actions
